import { useMemo } from "react";
import type { ChartOfAccountsTemplatePreviewEntity } from "@common/schemas/chart-of-accounts-template-apply.schema";
import type { SelectedPreviewEntity } from "./applyCoaTemplatePreviewEntries";
import type { PreviewOriginEntity } from "./cells/ApplyCoaPreviewNameCell";
import { mapPreviewEntityToCoaInputs } from "./applyCoaTemplatePreviewMapper";
import { ApplyCoaPreviewTable } from "./ApplyCoaPreviewTable";

interface Props {
    entity: ChartOfAccountsTemplatePreviewEntity;
    // The active entity being previewed, so the Origin column can render the
    // real company (name + avatar) for entity-sourced rows. Omitted by the
    // AddClient setup preview, which has no template<>entity diff and hides the
    // Origin column.
    entitySummary?: SelectedPreviewEntity;
}

// Live wizard apply preview. Maps the template<>entity diff payload into the
// shared presentational table in "preview" (future-tense) mode. All rendering
// logic lives in ApplyCoaPreviewTable, which the historical Activity drawer
// reuses in "historical" mode.
export const ApplyCoaTemplatePreviewCoa = ({
    entity,
    entitySummary,
}: Props) => {
    const { accounts, groups, newGroupIds, groupOriginById } = useMemo(
        () => mapPreviewEntityToCoaInputs(entity),
        [entity],
    );

    const originEntity: PreviewOriginEntity | undefined = useMemo(
        () =>
            entitySummary
                ? {
                      id: entitySummary.entityId,
                      name: entitySummary.name,
                      iconColor: entitySummary.iconColor,
                      workspaceId: entitySummary.workspaceId,
                      logo: entitySummary.logo,
                  }
                : undefined,
        [entitySummary],
    );

    return (
        <ApplyCoaPreviewTable
            mode="preview"
            accounts={accounts}
            groups={groups}
            newGroupIds={newGroupIds}
            groupOriginById={groupOriginById}
            originEntity={originEntity}
        />
    );
};
